"use client";

// useOsLayout — the user's by-hand organization of one level of the Internet
// OS, remembered per node path. This is the client side of the "manual law":
// anything set here was chosen by hand and outranks any auto-generated value.
//
// Durability model (hybrid): localStorage is the instant-paint + offline mirror;
// the os_layout table (migration 030, via /api/os/layout) is the cross-device
// source of truth. On mount we paint from localStorage, then reconcile from the
// server; every change writes localStorage immediately and flushes to the server
// debounced.

import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";

export interface ChildSummary {
  text: string;
  manual: true;
  edited_at: string;
}

import type { SavedView } from "./viewSpec";

export interface LayoutState {
  /** LEGACY hand-set free-desktop positions. No longer written or read — the
   *  home desktop computes its own arrangement. Kept so old rows survive a
   *  round-trip instead of being wiped by the merge below. */
  positions?: Record<string, { x: number; y: number }>;
  /**
   * orbit-view positions per child key — polar, spin-relative so a repositioned
   * tile keeps its spot as the orbit revolves: `a` = angle (radians) at zero
   * revolve, `r` = radius as a fraction of the orbit's outer radius. Namespaced
   * apart from `positions` so orbit and free-desktop never clash.
   */
  orbit?: Record<string, { a: number; r: number }>;
  /** hand-written summaries per child key — these win over auto summaries. */
  summaries?: Record<string, ChildSummary>;
  /** hand-picked glyphs per child key. */
  symbols?: Record<string, string>;
  /** view-chip order / custom chips for this level. */
  chips?: string[];
  /** VIEWS this person made here by saying how they wanted to look — the chip,
   *  the sentence they typed, and the spec netti read out of it. Saved at the
   *  level, so a view belongs to the place it was made for (lib/os/viewSpec). */
  views?: SavedView[];
  /** the active view chip for this level (home | grid | list | <custom>). */
  view?: string;
  /**
   * On the /home desktop: project keys HIDDEN from home. Home shows every
   * project by default (new ones included); hiding one drops it here, and
   * "send to home" removes it again. Keyed by projectId so it matches an item
   * wherever it's shown (home, the projects page, a folder view).
   */
  hidden?: string[];
}

const LS_PREFIX = "innernet-os:";
const lsKey = (path: string) => LS_PREFIX + path;

// One shared empty layout: "nothing saved" is the same object every time, so a
// state that is already empty is not re-rendered by being set to empty again.
const EMPTY: LayoutState = {};

const isRecord = (v: unknown): v is Record<string, unknown> => typeof v === "object" && v !== null && !Array.isArray(v);
const isNum = (v: unknown): v is number => typeof v === "number" && Number.isFinite(v);
const strings = (v: unknown): string[] | undefined => (Array.isArray(v) ? v.filter((x): x is string => typeof x === "string") : undefined);

/** keep the entries of a keyed map that pass `ok`; a map that isn't one is no map */
function entries<T>(v: unknown, ok: (x: unknown) => x is T): Record<string, T> | undefined {
  if (!isRecord(v)) return undefined;
  const out: Record<string, T> = {};
  for (const [k, x] of Object.entries(v)) if (k !== "__proto__" && ok(x)) out[k] = x;
  return out;
}

/**
 * Whatever came off localStorage or the wire, made into a LayoutState that is
 * safe to read: the merge below spreads `positions`, the chip bar maps `chips`,
 * the views bar maps `views`. A stored "null", an array, or a field of the wrong
 * type used to throw inside a state updater (a render crash on every folder, and
 * the same bad value was read back on every reload). Now each field is kept only
 * if it has the shape it is read with, and anything else is dropped, never thrown.
 */
export function parseLayoutState(raw: unknown): LayoutState {
  if (!isRecord(raw)) return EMPTY;
  const out: LayoutState = {};
  const positions = entries(raw.positions, (p): p is { x: number; y: number } => isRecord(p) && isNum(p.x) && isNum(p.y));
  if (positions) out.positions = positions;
  const orbit = entries(raw.orbit, (p): p is { a: number; r: number } => isRecord(p) && isNum(p.a) && isNum(p.r));
  if (orbit) out.orbit = orbit;
  // (what reads a summary is its text; `edited_at` is a stamp nothing looks up)
  const summaries = entries(raw.summaries, (p): p is ChildSummary => isRecord(p) && typeof p.text === "string" && p.manual === true);
  if (summaries) out.summaries = summaries;
  const symbols = entries(raw.symbols, (p): p is string => typeof p === "string");
  if (symbols) out.symbols = symbols;
  const chips = strings(raw.chips);
  if (chips) out.chips = chips;
  if (Array.isArray(raw.views)) {
    out.views = raw.views.filter(
      (v): v is SavedView => isRecord(v) && typeof v.id === "string" && typeof v.label === "string" && isRecord(v.spec),
    );
  }
  if (typeof raw.view === "string") out.view = raw.view;
  const hidden = strings(raw.hidden);
  if (hidden) out.hidden = hidden;
  return out;
}

function readLocal(path: string): LayoutState {
  if (typeof window === "undefined") return EMPTY;
  try {
    const raw = localStorage.getItem(lsKey(path));
    return raw ? parseLayoutState(JSON.parse(raw)) : EMPTY;
  } catch {
    return EMPTY;
  }
}

function writeLocal(path: string, state: LayoutState) {
  try {
    localStorage.setItem(lsKey(path), JSON.stringify(state));
  } catch {
    /* private mode / quota — server still holds the truth */
  }
}

// One debounce timer per path, shared across hook instances.
const timers = new Map<string, ReturnType<typeof setTimeout>>();

function flushRemote(path: string, state: LayoutState) {
  const existing = timers.get(path);
  if (existing) clearTimeout(existing);
  timers.set(
    path,
    setTimeout(() => {
      timers.delete(path);
      void fetch("/api/os/layout", {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ path, state }),
        keepalive: true,
      }).catch(() => {
        /* offline — localStorage already holds it; next change retries */
      });
    }, 600),
  );
}

// The server's read of one path, shared and abortable. Two things used to be
// wrong with a bare fetch per effect: a quick walk down three levels left three
// requests running whose answers nobody wanted, and a second reader of the same
// path (the shell and a room open together) asked again. Now one request serves
// every reader of a path, and it is abandoned when the last of them walks away.
// A stalled server is let go of after 8s: the local copy is what the page keeps.
export const REMOTE_DEADLINE_MS = 8000;
type Pending = { p: Promise<LayoutState | null>; ac: AbortController; refs: number };
const pending = new Map<string, Pending>();

export function readRemote(path: string): { p: Promise<LayoutState | null>; release: () => void } {
  let e = pending.get(path);
  if (!e) {
    const ac = new AbortController();
    const timer = setTimeout(() => ac.abort(), REMOTE_DEADLINE_MS);
    const entry: Pending = {
      ac,
      refs: 0,
      p: fetch(`/api/os/layout?path=${encodeURIComponent(path)}`, { signal: ac.signal })
        .then((r) => (r.ok ? r.json() : null))
        .then((data) => (data ? parseLayoutState(data.state) : null))
        .catch(() => null)
        .finally(() => {
          clearTimeout(timer);
          if (pending.get(path) === entry) pending.delete(path);
        }),
    };
    pending.set(path, entry);
    e = entry;
  }
  const mine = e;
  mine.refs++;
  let released = false;
  return {
    p: mine.p,
    release: () => {
      if (released) return;
      released = true;
      mine.refs--;
      if (mine.refs <= 0) {
        mine.ac.abort();
        if (pending.get(path) === mine) pending.delete(path);
      }
    },
  };
}

const useIso = typeof window !== "undefined" ? useLayoutEffect : useEffect;

export function useOsLayout(path: string) {
  // Start EMPTY so server HTML and the client's first render match (no hydration
  // mismatch); the saved layout is read before the first PAINT (the layout effect
  // below), so the default view never shows for a frame first. A chip order saved
  // for a heavy view used to mount the default one, then swap.
  const [state, setState] = useState<LayoutState>(EMPTY);
  const [ready, setReady] = useState(false);
  const stateRef = useRef(state);
  stateRef.current = state;

  useIso(() => {
    setState(readLocal(path));
  }, []);

  // Descending a level (a new path) swaps the layout DURING the render that
  // carries the new path — React redoes this render before anything commits — so
  // the level is never drawn once with the previous level's saved view.
  const [forPath, setForPath] = useState(path);
  if (forPath !== path) {
    setForPath(path);
    setState(readLocal(path));
    setReady(false);
  }

  // Reconcile from the server once per path. The server is source of truth, but
  // we keep any local-only keys the server hasn't seen yet (optimistic writes).
  useEffect(() => {
    let alive = true;
    const { p, release } = readRemote(path);
    p.then((remote) => {
      if (!alive) return;
      if (remote) {
        setState((local) => {
          const merged: LayoutState = {
            positions: { ...remote.positions, ...local.positions },
            orbit: { ...remote.orbit, ...local.orbit },
            summaries: { ...remote.summaries, ...local.summaries },
            symbols: { ...remote.symbols, ...local.symbols },
            chips: local.chips ?? remote.chips,
            view: local.view ?? remote.view,
            hidden: local.hidden ?? remote.hidden,
            // (views are the one field the merge never carried; the server's stay the server's)
            views: local.views ?? remote.views,
          };
          writeLocal(path, merged);
          return merged;
        });
      }
      setReady(true);
    });
    return () => {
      alive = false;
      release();
    };
  }, [path]);

  // Apply a partial mutation: update memory, mirror to localStorage, debounce-sync.
  const mutate = useCallback(
    (fn: (prev: LayoutState) => LayoutState) => {
      setState((prev) => {
        const next = fn(prev);
        writeLocal(path, next);
        flushRemote(path, next);
        return next;
      });
    },
    [path],
  );

  // NOTE: there is deliberately no setPosition/clearPosition any more. The home
  // desktop arranges itself (DirectoryView's layout law) — hand-placing folders
  // was what put them in a heap in the top-left corner. `positions` survives on
  // the type and in the merge below only so existing os_layout rows round-trip
  // rather than being wiped; nothing reads it.

  const setOrbitPosition = useCallback(
    (key: string, pos: { a: number; r: number }) =>
      mutate((s) => ({ ...s, orbit: { ...s.orbit, [key]: pos } })),
    [mutate],
  );

  const clearOrbitPosition = useCallback(
    (key: string) =>
      mutate((s) => {
        const orbit = { ...s.orbit };
        delete orbit[key];
        return { ...s, orbit };
      }),
    [mutate],
  );

  const setSummary = useCallback(
    (key: string, text: string) =>
      mutate((s) => {
        const summaries = { ...s.summaries };
        const trimmed = text.trim();
        if (trimmed) summaries[key] = { text: trimmed, manual: true, edited_at: new Date().toISOString() };
        else delete summaries[key];
        return { ...s, summaries };
      }),
    [mutate],
  );

  const setSymbol = useCallback(
    (key: string, glyph: string) =>
      mutate((s) => ({ ...s, symbols: { ...s.symbols, [key]: glyph } })),
    [mutate],
  );

  const setView = useCallback((view: string) => mutate((s) => ({ ...s, view })), [mutate]);
  /** add / replace / drop a made view at this level. */
  const setViews = useCallback((views: SavedView[]) => mutate((s) => ({ ...s, views })), [mutate]);
  const setChips = useCallback((chips: string[]) => mutate((s) => ({ ...s, chips })), [mutate]);

  // Home membership: add/remove a project key from the hidden set. Used by the
  // shared context menu ("Hide from home" / "Send to home") — see DirectoryView.
  const setHidden = useCallback(
    (key: string, hidden: boolean) =>
      mutate((s) => {
        const set = new Set(s.hidden ?? []);
        if (hidden) set.add(key);
        else set.delete(key);
        return { ...s, hidden: [...set] };
      }),
    [mutate],
  );

  return { state, ready, setOrbitPosition, clearOrbitPosition, setSummary, setSymbol, setView, setViews, setChips, setHidden };
}

export default useOsLayout;
